import React, { useState, useEffect } from 'react';
import { Button, List, Space, Modal, message, Typography, Card, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined } from '@ant-design/icons';
import { TestData } from '../../types/testItem';
import TestDataForm from './TestDataForm';
import { SelectFolderModal } from '../shared/SelectFolderModal';
import { Folder } from '../../types/testItem';
import { testItemService } from '../../services/testItemService';
import { useTranslation } from 'react-i18next';
import { useProject } from '../../contexts/ProjectContext';

interface Props {
    testItemId: number;
    folderId?: number;
    onDataChange?: () => void;
}

export const TestDataList: React.FC<Props> = ({
    testItemId,
    folderId,
    onDataChange,
}) => {
    const [testData, setTestData] = useState<TestData[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedData, setSelectedData] = useState<TestData | undefined>();
    const [isFolderModalVisible, setIsFolderModalVisible] = useState(false);
    const [dataToAddToFolder, setDataToAddToFolder] = useState<TestData | null>(null);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const { currentProject } = useProject();

    const fetchData = async () => {
        if (!currentProject?.id) return;
        
        setLoading(true);
        try {
            const [data, folderList] = await Promise.all([
                testItemService.getTestData(testItemId),
                testItemService.getFolders(currentProject.id)
            ]);
            setTestData(data);
            setFolders(folderList);
        } catch (error) {
            console.error('Error fetching test data:', error);
            message.error(t('testData.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [testItemId, currentProject?.id]);

    const handleEdit = (data: TestData) => {
        setSelectedData(data);
        setIsModalVisible(true);
    };

    const handleCreate = () => {
        setSelectedData(undefined);
        setIsModalVisible(true);
    };

    const handleDelete = async (dataId: number) => {
        Modal.confirm({
            title: t('testData.confirmDelete'),
            content: t('testData.confirmDeleteMessage'),
            okText: t('common.yes'),
            okType: 'danger',
            cancelText: t('common.no'),
            onOk: async () => {
                try {
                    await testItemService.deleteTestData(testItemId, dataId);
                    message.success(t('testData.deleteSuccess'));
                    // Refresh the data after successful deletion
                    fetchData();
                    if (onDataChange) {
                        onDataChange();
                    }
                } catch (error) {
                    console.error('Error deleting test data:', error);
                    message.error(t('testData.deleteError'));
                }
            }
        });
    };

    const handleSubmit = async (data: Omit<TestData, 'id'> | TestData) => {
        if (!testItemId || testItemId === 0) return;

        try {
            let response;
            if (selectedData) {
                response = await testItemService.updateTestData(testItemId, selectedData.id, data);
                message.success('Данные обновлены');
            } else {
                response = await testItemService.createTestData(testItemId, data);
                message.success('Данные созданы');
            }
            setIsModalVisible(false);
            const updatedData = await testItemService.getTestData(testItemId);
            setTestData(updatedData);
            if (onDataChange) {
                onDataChange();
            }
            return response;
        } catch (error) {
            message.error('Не удалось сохранить тестовые данные');
            throw error;
        }
    };

    const handleAddToFolderClick = (data: TestData) => {
        setDataToAddToFolder(data);
        setIsFolderModalVisible(true);
    };

    const handleFolderSelect = async (folder: Folder | number) => {
        const folderId = typeof folder === 'number' ? folder : folder.id;
        if (!testItemId || testItemId === 0 || !dataToAddToFolder) return;

        try {
            await testItemService.updateTestData(testItemId, dataToAddToFolder.id, {
                ...dataToAddToFolder,
                folder_id: folderId
            });
            message.success('Данные добавлены в папку');
            setIsFolderModalVisible(false);
            if (onDataChange) {
                onDataChange();
            }
        } catch (error) {
            message.error('Не удалось добавить данные в папку');
        }
    };

    // Don't render anything if testItemId is 0 or undefined
    if (!testItemId || testItemId === 0) {
        return null;
    }

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Добавить данные
                </Button>
            </Space>

            <List
                loading={loading}
                dataSource={testData}
                renderItem={data => (
                    <List.Item
                        actions={[
                            <Button
                                type="text"
                                icon={<FolderOutlined />}
                                onClick={() => handleAddToFolderClick(data)}
                            >
                                Добавить к папке
                            </Button>,
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(data)}
                            >
                                Редактировать
                            </Button>,
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(data.id)}
                            >
                                Удалить
                            </Button>
                        ]}
                    >
                        <List.Item.Meta
                            title={data.name}
                            description={
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {data.description && (
                                        <Typography.Text type="secondary">
                                            {data.description}
                                        </Typography.Text>
                                    )}
                                    <Card size="small">
                                        <Typography.Text>
                                            {JSON.stringify(data.data, null, 2)}
                                        </Typography.Text>
                                    </Card>
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />

            <TestDataForm
                projectId={testItemId}
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSuccess={handleSubmit}
            />

            <SelectFolderModal
                open={isFolderModalVisible}
                onClose={() => setIsFolderModalVisible(false)}
                folders={folders}
                onSelect={handleFolderSelect}
            />
        </div>
    );
}; 